// v4.35 - WEBSITE-SIGNIN-NO-BANNER-01 - 2026-10-29 - one "Cancel" link under the request-early-access form, to "/" (existing key common:actions.cancel). Nothing else on the page changes.
// v4.00 - Add company_name field to waitlist form - 2026-03-24
// v3.93 - Dark mode support: theme-aware bgcolor + transparent logo replacing text placeholder - 2026-03-19
// v3.85 - MKTG-03: Replace mailto: with real API call to /api/waitlist/join - 2026-03-16
// v3.68 - PILOT-01: WaitlistPage — shown at /register and /demo-register when REACT_APP_REGISTRATION_OPEN=false - 2026-03-10
import React, { useState } from 'react';
import MyLojicLogo from '../components/MyLojicLogo';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Button,
  Container,
  Typography,
  Paper,
  TextField,
  Link,
  Alert,
  CircularProgress,
} from '@mui/material';
import { MailOutline as MailIcon } from '@mui/icons-material';
import { colors } from '../theme/colors';
import { useTheme } from '@mui/material/styles'; // v3.93
import { portalRequestUngated } from '../services/portalRequest'; // v4.28 - G10-B: one request wrapper, carries the chosen company
import { useTranslation } from 'react-i18next'; // v4.35 - WEBSITE-SIGNIN-NO-BANNER-01: Cancel label

const WaitlistPage: React.FC = () => {
  const muiTheme = useTheme(); // v3.93
  const isDark = muiTheme.palette.mode === 'dark';
  const { t } = useTranslation('common'); // v4.35 - WEBSITE-SIGNIN-NO-BANNER-01
  const [name, setName] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleJoinWaitlist = async () => {
    if (!name.trim() || !companyName.trim() || !email.trim() || !email.includes('@')) return;
    setLoading(true);
    setError('');
    try {
      const res = await portalRequestUngated('/api/waitlist/join', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: name.trim(),
          company_name: companyName.trim(),
          email: email.trim().toLowerCase(),
          source: 'contact_form',
        }),
      });
      if (res.ok) {
        setSubmitted(true);
      } else {
        const data = await res.json().catch(() => ({}));
        setError(data.detail || data.error || 'Something went wrong. Please try again.');
      }
    } catch {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: 'background.default',
        py: 4,
      }}
    >
      <Container maxWidth="sm">
        <Paper elevation={3} sx={{ p: { xs: 3, sm: 6 }, borderRadius: 2 }}>

          {/* Logo - v3.93 - transparent logo replacing text placeholder */}
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
            <MyLojicLogo isDark={isDark} height={48} />
          </Box>

          {/* Headline */}
          <Typography
            variant="h5"
            sx={{
              color: colors.text.primary,
              fontWeight: 600,
              textAlign: 'center',
              mb: 1.5,
            }}
          >
            We're currently invite-only
          </Typography>

          {/* Warm description */}
          <Typography
            variant="body1"
            sx={{
              color: colors.text.secondary,
              textAlign: 'center',
              mb: 4,
              lineHeight: 1.7,
            }}
          >
            MyLojic is in private pilot. We're onboarding new customers in small
            waves so every team gets the attention they deserve. Want to be next?
          </Typography>

          {/* Success state */}
          {submitted ? (
            <Alert
              severity="success"
              icon={<MailIcon />}
              sx={{ mb: 3 }}
            >
              <strong>You're on the list!</strong> We'll be in touch soon.
            </Alert>
          ) : (
            <Box>
              {/* Name input */}
              <TextField
                fullWidth
                label="Your name"
                placeholder="Jane Smith"
                value={name}
                onChange={(e) => setName(e.target.value)}
                autoFocus
                disabled={loading}
                sx={{ mb: 2 }}
              />

              {/* Company name input — v4.00 */}
              <TextField
                fullWidth
                label="Company name"
                placeholder="Acme Inc."
                value={companyName}
                onChange={(e) => setCompanyName(e.target.value)}
                required
                disabled={loading}
                sx={{ mb: 2 }}
              />

              {/* Email input */}
              <TextField
                fullWidth
                label="Your email address"
                placeholder="you@yourcompany.com"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="email"
                disabled={loading}
                sx={{ mb: 2 }}
              />

              {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {error}
                </Alert>
              )}

              {/* CTA button */}
              <Button
                variant="contained"
                fullWidth
                size="large"
                onClick={handleJoinWaitlist}
                disabled={loading || !name.trim() || !companyName.trim() || !email || !email.includes('@')}
                sx={{
                  bgcolor: colors.primary.main,
                  color: colors.primary.contrastText,
                  py: 1.5,
                  fontSize: '1rem',
                  fontWeight: 600,
                  textTransform: 'none',
                  '&:hover': { bgcolor: colors.primary.dark },
                  '&:disabled': { bgcolor: colors.border.main },
                }}
              >
                {loading ? <CircularProgress size={22} color="inherit" /> : 'Request early access'}
              </Button>

              {/* v4.35 - WEBSITE-SIGNIN-NO-BANNER-01: Cancel returns to the home page */}
              <Box sx={{ textAlign: 'center', mt: 2 }}>
                <Link
                  component={RouterLink}
                  to="/"
                  data-testid="waitlist-cancel"
                  sx={{
                    color: colors.text.secondary,
                    textDecoration: 'none',
                    fontSize: '0.875rem',
                    '&:hover': { textDecoration: 'underline' },
                  }}
                >
                  {t('common:actions.cancel')}
                </Link>
              </Box>
            </Box>
          )}

          {/* Already have an account */}
          <Typography
            variant="body2"
            sx={{
              textAlign: 'center',
              mt: 4,
              color: colors.text.secondary,
            }}
          >
            Already have an account?{' '}
            <Link
              component={RouterLink}
              to="/login"
              sx={{
                color: colors.primary.main,
                fontWeight: 600,
                textDecoration: 'none',
                '&:hover': { textDecoration: 'underline' },
              }}
            >
              Sign in
            </Link>
          </Typography>

        </Paper>
      </Container>
    </Box>
  );
};

export default WaitlistPage;
